import React, { useMemo, ElementType } from 'react';
import classNames from 'classnames';
import { get } from 'lodash';
import { excludeChildrenByType, findChildByType } from '@fiverr-private/ui_utils';
import { AvatarProps } from './types';
import { getAvatarBackgroundColor, getRingStyle } from './utils';
import classes from './index.module.scss';
import { AvatarContext } from './AvatarContext';
import { AVATAR_SIZES, RING_SIZE_FACTOR } from './constants';

const Avatar = ({
  color = 'auto',
  username,
  onClick,
  size = 'xs',
  showRing = false,
  showTitle = true,
  displayName,
  children,
}: AvatarProps) => {
  const isOnClickValid = typeof onClick === 'function';
  const Element: ElementType = isOnClickValid ? 'button' : 'div';

  const avatarBackgroundColor = useMemo(() => getAvatarBackgroundColor({ color, username }), [color, username]);
  const avatarSize = AVATAR_SIZES[size];
  const ringSize = useMemo(
    () => Math.round(avatarSize * RING_SIZE_FACTOR), // The ring needs to be 1/20 of the size of the avatar
    [avatarSize]
  );
  const avatarFontSize = useMemo(
    () =>
      // We need to subtract the ring width from the four sides of the avatar container to get the correct size.
      showRing ? avatarSize - ringSize * 4 : avatarSize,
    [showRing, avatarSize, ringSize]
  );
  const ringStyle = useMemo(() => getRingStyle({ showRing, ringSize }), [showRing, ringSize]);
  const avatarStyle = {
    fontSize: avatarFontSize,
    backgroundColor: avatarBackgroundColor,
  };

  const nameValue = displayName || username;
  const firstLetter = get(nameValue, '[0]', '');

  return (
    <AvatarContext.Provider
      value={{
        size,
        nameValue,
      }}
    >
      <Element className={classNames(classes.container)} style={ringStyle} onClick={onClick} data-testid="avatar">
        <figure style={avatarStyle} className={classes.figure} title={showTitle ? nameValue : ''}>
          <figcaption className={classes.caption}>{firstLetter}</figcaption>
          {findChildByType(children, 'AvatarImage')}
        </figure>
        {excludeChildrenByType(children, 'AvatarImage')}
      </Element>
    </AvatarContext.Provider>
  );
};

Avatar.id = 'Avatar';
Avatar.displayName = 'Avatar';

export default Avatar;
